import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BillingApiAccountResponseDto, BillingApiMerchantResponseDto } from '@lingo2-billing-sdk/models';
import { DestroyableComponent } from '@models/destroyable.component';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';

const billing_v2_url = `${environment.account_url}/billing/v2`;

export interface IBillingPaymentProvider {
  id: string;
  label: string;
}

export interface IBillingCardPaymentMethod {
  id: string;
  cardAccount: IBillingCardAccount;
}

export interface IBillingPaymentMethod {
  /** Для оплаты сохранённым способом оплаты */
  paymentMethod?: BillingApiAccountResponseDto;
  /** Для оплаты новом способом оплаты с использованием указанного провайдера */
  provider?: BillingApiMerchantResponseDto;
}

export interface IBillingCardAccount {
  label: string;
  brand: string;
  expiresAt: string;
}

export interface IBillingPayment {
  id: string;
  status: string;
  url?: string;
}

export interface IBillingCharge {
  id: string;
  amount: string;
  status: string;
}

export interface IBillingCustomer {
  /** Имя плательщика */
  firstName?: string;

  /** Фамилия плательщика */
  lastName?: string;

  /** Email плательщика */
  email?: string;

  /** Телефон плательщика */
  phone?: string;

  /** ISO-код страны (двухзначный) */
  countryCode?: string;
}

// TODO Реализовать работу через @lingo2-billing-sdk/angular
// либо вообще заменить данный сервис на использование только сервисов
// @lingo2-billing-sdk/angular внутри платёжного компонента
@Injectable({
  providedIn: 'root',
})
export class PaymentWidgetBillingService extends DestroyableComponent {
  public constructor(private http: HttpClient, private translate: TranslateService) {
    super();
  }

  /**
   * Сохранённые способы оплаты
   */
  public getPaymentMethods() {
    const url = `${billing_v2_url}/account?type=BANK&purpose=PAYMENT`;
    return this.http
      .get<IBillingPaymentMethod[]>(url, { observe: 'response' })
      .pipe(map((response) => this.handleBillingResponse<IBillingPaymentMethod[]>(response)));
  }

  /**
   * Информация о платеже
   * (для отслеживания статуса оплаты)
   */
  public getPayment(id: string) {
    const url = `${billing_v2_url}/payment/` + id;
    return this.http
      .get<IBillingPayment>(url, { observe: 'response' })
      .pipe(map((response) => this.handleBillingResponse<IBillingPayment>(response)));
  }

  /**
   * Пакетная оплата начислений >=1
   */
  public payBulk(
    charges: string[],
    paymentMethod: IBillingPaymentMethod,
    customer: IBillingCustomer,
  ): Observable<IBillingPayment> {
    const payload = charges.map((id) => ({
      chargeId: id,
      firstName: customer.firstName,
      lastName: customer.lastName,
      countryCode: customer.countryCode,
      ...(customer.email ? { email: customer.email } : null),
      ...(paymentMethod?.provider?.id
        ? {
            providerId: paymentMethod.provider.id,
          }
        : paymentMethod?.paymentMethod?.id
        ? {
            accountId: paymentMethod.paymentMethod.id,
          }
        : null),
    }));
    const url = `${billing_v2_url}/charge/pay`;
    return this.http
      .post<any>(url, payload, {
        observe: 'response',
      })
      .pipe(
        map((response) =>
          this.handleBillingResponse<any>(response, (data) => {
            if (!data[0] || !data[0].payment) {
              throw new Error();
            }

            return {
              id: data[0].payment.id,
              status: data[0].payment.status,
              url: environment.billing_public_url + '/' + data[0].payment.paymentUrl,
            };
          }),
        ),
      );
  }

  /**
   * Загрузка информации о начислениях
   */
  public getCharges(chargeId: string | string[]) {
    const url = `${billing_v2_url}/charge/`;
    return this.http
      .get<IBillingCharge[]>(url, {
        observe: 'response',
        params: {
          'id[]': chargeId,
        },
      })
      .pipe(map((response) => this.handleBillingResponse<IBillingCharge[]>(response)));
  }

  public getPaymentProviders() {
    const url = `${billing_v2_url}/merchant/`;
    return this.http
      .get<IBillingPaymentProvider[]>(url, {
        observe: 'response',
      })
      .pipe(map((response) => this.handleBillingResponse<IBillingPaymentProvider[]>(response)));
  }

  private handleBillingResponse<T>(response: HttpResponse<T>, transformer?: (arg: any) => any): T {
    if (transformer) {
      return transformer(response.body);
    } else {
      return response.body;
    }
  }
}
